<script setup>
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from '../../../../../../composables/useAccount';
import {
  conversationUrl,
  frontendURL,
} from '../../../../../../helper/URLHelper';

const props = defineProps({
  failedItems: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const { accountId } = useAccount();

const errorSearchQuery = ref('');
const expandedErrorIds = ref(new Set());
const canExpandError = ref({});
const errorTextRefs = ref({});

const filteredFailedItems = computed(() => {
  if (!errorSearchQuery.value) return props.failedItems;
  const query = errorSearchQuery.value.toLowerCase();
  return props.failedItems.filter(item => {
    const idMatch = String(item.conversation_id).includes(query);
    const textMatch = item.error && item.error.toLowerCase().includes(query);
    return idMatch || textMatch;
  });
});

const toggleErrorDetails = id => {
  if (expandedErrorIds.value.has(id)) {
    expandedErrorIds.value.delete(id);
  } else {
    expandedErrorIds.value.add(id);
  }
};

const isErrorExpanded = id => expandedErrorIds.value.has(id);

const setTextRef = (el, id) => {
  if (el) errorTextRefs.value[id] = el;
};

const checkOverflows = () => {
  Object.entries(errorTextRefs.value).forEach(([id, el]) => {
    if (el) {
      if (isErrorExpanded(Number(id))) {
        canExpandError.value[id] = true;
      } else {
        canExpandError.value[id] = el.scrollWidth > el.clientWidth;
      }
    }
  });
};

const getConversationRoute = conversationId => {
  const relativePath = conversationUrl({
    accountId: accountId.value,
    id: conversationId,
  });
  return frontendURL(relativePath);
};

let resizeObserver = null;

onMounted(async () => {
  await nextTick();
  checkOverflows();

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => checkOverflows());
    const container = document.querySelector('.js-error-table-container');
    if (container) resizeObserver.observe(container);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

// Пересчитываем стрелочки при поиске
watch(filteredFailedItems, async () => {
  await nextTick();
  checkOverflows();
});
</script>

<template>
  <div class="flex flex-col gap-3 js-error-table-container">
    <h4 class="text-xs font-semibold uppercase tracking-wide text-n-slate-11">
      {{ t('BROADCASTS.ERRORS') }}
    </h4>

    <input
      v-if="failedItems.length > 0"
      v-model="errorSearchQuery"
      type="text"
      :placeholder="
        t(
          'BROADCASTS.SEARCH_ERROR_PLACEHOLDER',
          'Search by ID or error text...'
        )
      "
      class="w-full px-3 py-1.5 text-xs bg-n-alpha-1 border border-n-strong rounded-md text-n-slate-12 focus:outline-none focus:border-red-500 mb-1"
    />

    <div
      v-if="filteredFailedItems.length"
      class="border border-n-strong rounded-lg overflow-hidden flex flex-col"
    >
      <div class="overflow-y-auto max-h-80 w-full scrollbar-thin">
        <table class="w-full text-left border-collapse">
          <thead
            class="bg-n-alpha-1 text-xs font-semibold text-n-slate-11 select-none sticky top-0 z-10"
          >
            <tr>
              <th
                class="w-[120px] py-2 px-4 border-r border-b border-n-strong text-center sticky top-0 bg-n-alpha-1 z-10 shadow-[0_1px_0_var(--n-strong)]"
              >
                {{ t('BROADCASTS.CONVERSATION_ID') }}
              </th>
              <th
                class="px-3 py-2 border-b border-n-strong sticky top-0 bg-n-alpha-1 z-10 shadow-[0_1px_0_var(--n-strong)]"
              >
                {{ t('BROADCASTS.ERROR_REASON') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-xs">
            <tr
              v-for="failedItem in filteredFailedItems"
              :key="failedItem.conversation_id"
              class="border-b border-n-strong last:border-0 group"
            >
              <td
                class="bg-n-alpha-2 border-r border-n-strong w-[120px] align-middle py-2 px-2 text-center"
              >
                <a
                  :href="getConversationRoute(failedItem.conversation_id)"
                  class="font-semibold text-blue-600 hover:text-blue-700 hover:underline text-sm whitespace-nowrap block"
                >
                  #{{ failedItem.conversation_id }}
                </a>
              </td>

              <td class="bg-red-500/10 px-3 py-2 align-top">
                <div class="flex items-start gap-3 min-w-0">
                  <div class="shrink-0 text-red-600 mt-0.5">
                    <span class="block w-4 h-4 i-lucide-alert-circle" />
                  </div>

                  <div class="flex-1 min-w-0">
                    <div
                      :ref="el => setTextRef(el, failedItem.conversation_id)"
                      class="text-n-slate-12 font-mono text-base leading-tight break-all"
                      :class="{
                        truncate: !isErrorExpanded(failedItem.conversation_id),
                      }"
                    >
                      {{ failedItem.error }}
                    </div>
                  </div>

                  <button
                    v-if="canExpandError[failedItem.conversation_id]"
                    class="shrink-0 text-n-slate-11 hover:text-n-slate-12 transition-colors flex items-center justify-center w-5 h-5 mt-0.5"
                    @click="toggleErrorDetails(failedItem.conversation_id)"
                  >
                    <span
                      class="block w-4 h-4 i-lucide-chevron-down transition-transform duration-200"
                      :class="{
                        'rotate-180': isErrorExpanded(
                          failedItem.conversation_id
                        ),
                      }"
                    />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-else-if="failedItems.length > 0"
      class="text-xs text-n-slate-11 italic"
    >
      {{ t('BROADCASTS.NO_MATCHING_ERRORS', 'No matching errors found') }}
    </div>

    <div v-else class="text-xs text-n-slate-11">
      {{ t('BROADCASTS.NO_FAILED_ITEMS') }}
    </div>
  </div>
</template>
